import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import defaultImg from '../../asset/project/default_1.png';
import deleteIcon from '../../asset/icon/delete.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { FormationItemData } from '../../shared/dto/EntityRepository';
import {
  FormationImageMode,
  FormationType,
} from '../../shared/enum/EnumRepository';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import {
  ImportStationSelectedData,
  useImportStationWorkStore,
} from '../../shared/store/importStationWorkStore';
import {
  getFormationItemSource,
  getFormationImageMode,
  getFormationImagePosition,
  resolveFormationSlotImage,
} from '../../shared/util/formationUtils';
import { getFormationSlotAspectRatio } from '../projectElement/formation/formationGrid';

const SLOT_PREVIEW_HEIGHT = 120;

interface FormationImportSelectionSlotsProps {
  formationItems: FormationItemData[];
  formationType: FormationType;
}

interface FormationImportSlotProps {
  formationItem: FormationItemData;
  formationType: FormationType;
  selectedItem?: ImportStationSelectedData;
}

const FormationImportSlot: React.FC<FormationImportSlotProps> = ({
  formationItem,
  formationType,
  selectedItem,
}) => {
  const [isHover, setIsHover] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const removeSelectedData = useImportStationWorkStore((s) => s.removeSelectedData);
  const replacementSlotIndexes = useImportStationWorkStore(
    (s) => s.formationReplacementSlotIndexes,
  );
  const addReplacementSlotIndex = useImportStationWorkStore(
    (s) => s.addFormationReplacementSlotIndex,
  );
  const existingImage = resolveFormationSlotImage(formationItem);
  const hasExistingContent = getFormationItemSource(formationItem) !== 'empty';
  const slotIndex = formationItem.slotIndex;
  const isReplacingExisting = replacementSlotIndexes.includes(slotIndex);
  const showExisting = hasExistingContent && !isReplacingExisting;
  const originUrl = selectedItem?.originUrl
    ?? (showExisting ? existingImage?.originUrl : '')
    ?? '';
  const imageUrl = useOptimizedImageUrl(originUrl, imageRef);
  const imageMode = showExisting
    ? getFormationImageMode(formationItem)
    : FormationImageMode.FIT;
  const imagePosition = showExisting
    ? getFormationImagePosition(formationItem)
    : { xPercent: 50, yPercent: 50 };
  const slotRatio = getFormationSlotAspectRatio(formationType, slotIndex);
  const aspectRatio = slotRatio.width / slotRatio.height;
  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedItem) {
      removeSelectedData(selectedItem);
      return;
    }
    if (showExisting) addReplacementSlotIndex(slotIndex);
  };

  return (
    <Slot
      $aspectRatio={aspectRatio}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {showExisting || selectedItem ? (
        <Preview ref={imageRef} $aspectRatio={aspectRatio}>
          <PreviewImage
            $imageMode={imageMode}
            $isHover={isHover}
            $positionX={imagePosition.xPercent}
            $positionY={imagePosition.yPercent}
            src={imageUrl || defaultImg}
            alt={selectedItem
              ? selectedItem.sourceDetail?.description
                || selectedItem.title
                || `Slot ${slotIndex + 1}`
              : existingImage?.alt || `Slot ${slotIndex + 1}`}
          />
          {(showExisting || selectedItem) && (
            <HoverOverlay $isHover={isHover} onClick={handleRemove}>
              <UniversalIconBtn
                size="fit"
                border={false}
                isTransparent={true}
                backgroundImg={deleteIcon}
                onClick={handleRemove}
                aria-label={`Remove selection from Slot ${slotIndex + 1}`}
              />
            </HoverOverlay>
          )}
        </Preview>
      ) : (
        <EmptySlot
          $aspectRatio={aspectRatio}
          aria-label={`Slot ${slotIndex + 1} is empty`}
        >
          +
        </EmptySlot>
      )}
    </Slot>
  );
};

const FormationImportSelectionSlots: React.FC<FormationImportSelectionSlotsProps> = ({
  formationItems,
  formationType,
}) => {
  const selectedDataList = useImportStationWorkStore((s) => s.selectedDataList);
  const sortedItems = [...formationItems].sort((a, b) => a.slotIndex - b.slotIndex);

  return (
    <Slots aria-label="Formation import selections">
      {sortedItems.map((formationItem) => (
        <FormationImportSlot
          key={formationItem.slotIndex}
          formationItem={formationItem}
          formationType={formationType}
          selectedItem={selectedDataList.find(
            (item) => item.formationSlotIndex === formationItem.slotIndex,
          )}
        />
      ))}
    </Slots>
  );
};

const Slots = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  overflow-x: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slot = styled.div<{ $aspectRatio: number }>`
  position: relative;
  flex: 0 0 ${({ $aspectRatio }) => `${SLOT_PREVIEW_HEIGHT * $aspectRatio}px`};
  width: ${({ $aspectRatio }) => `${SLOT_PREVIEW_HEIGHT * $aspectRatio}px`};
  display: flex;
  flex-direction: column;
`;

const Preview = styled.div<{ $aspectRatio: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const PreviewImage = styled.img<{
  $imageMode: FormationImageMode;
  $isHover: boolean;
  $positionX: number;
  $positionY: number;
}>`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: ${({ $imageMode }) => (
    $imageMode === FormationImageMode.FIT ? 'contain' : 'cover'
  )};
  object-position: ${({ $positionX, $positionY }) => `${$positionX}% ${$positionY}%`};
  filter: ${({ $isHover }) => ($isHover ? 'blur(2px)' : 'none')};
  transition: filter 0.2s ease;
`;

const HoverOverlay = styled.div<{ $isHover: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isHover }) => ($isHover ? 1 : 0)};
  transition: opacity 0.2s ease;
  cursor: pointer;
`;

const EmptySlot = styled.div<{ $aspectRatio: number }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_2};
`;

export default FormationImportSelectionSlots;
